import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Modal, ModalHeader, ModalBody } from "reactstrap";
import { auth } from "../../pages/api/axios";
import "./sessionWarningBanner.scss";

function currentAccountEmail() {
    try {
        return JSON.parse(window.localStorage.getItem("authUser") || "null")?.email || "";
    } catch (_) {
        return "";
    }
}

function recoveryError(error, verifyingOtp) {
    if (error?.code === "AUTH_SESSION_CHANGED") {
        return "Your session changed in another tab. Please enter your password again to continue with this account.";
    }
    const status = error?.response?.status;
    if (!error?.response) {
        return "We couldn't connect. Check your internet connection and try again. Keep this page open to retain your work.";
    }
    if (status === 429) return "Too many attempts. Please wait a moment before trying again.";
    if (status >= 500) return "We couldn't renew your session right now. Please try again in a moment.";
    return error.response.data?.message || (verifyingOtp
        ? "That code could not be verified. Check the code or request a new one."
        : "That password could not be verified. Please try again.");
}

// Reactstrap 8 forwards arbitrary ARIA props to role=document rather than the
// dialog itself. Set these on its documented innerRef for screen readers.
function describeRecoveryDialog(node) {
    if (!node) return;
    node.setAttribute("aria-modal", "true");
    node.setAttribute("aria-describedby", "rdc-session-recovery-description");
}

// This is a sibling of the routed page, never a replacement for it. Closing
// recovery after authentication therefore preserves local form state and files.
const SessionRecoveryModal = ({ reason, email: recoveryEmail }) => {
    const [email] = useState(() => recoveryEmail || currentAccountEmail());
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [otp, setOtp] = useState("");
    const [challenge, setChallenge] = useState(null);
    const [otpChannel, setOtpChannel] = useState("sms");
    const [resendSeconds, setResendSeconds] = useState(0);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState("");
    const [confirmSignOut, setConfirmSignOut] = useState(false);
    const passwordRef = useRef(null);
    const otpRef = useRef(null);
    const keepWorkingRef = useRef(null);
    const requestInFlight = useRef(false);
    const mounted = useRef(true);

    useEffect(() => () => { mounted.current = false; }, []);

    const focusInput = () => {
        const target = confirmSignOut ? keepWorkingRef.current : challenge ? otpRef.current : passwordRef.current;
        if (target) target.focus();
    };

    useEffect(() => {
        const target = confirmSignOut ? keepWorkingRef.current : challenge ? otpRef.current : passwordRef.current;
        if (target) target.focus();
    }, [challenge, confirmSignOut, busy]);

    useEffect(() => {
        if (resendSeconds <= 0) return undefined;
        const timeout = setTimeout(() => setResendSeconds((seconds) => seconds - 1), 1000);
        return () => clearTimeout(timeout);
    }, [resendSeconds]);

    const renewSession = async ({ channel = otpChannel, resend = false } = {}) => {
        if (requestInFlight.current) return;
        if (!password) {
            setError("Enter your password to continue.");
            passwordRef.current?.focus();
            return;
        }
        if (challenge && !resend && !/^\d{6}$/.test(otp)) {
            setError("Enter the 6-digit verification code.");
            otpRef.current?.focus();
            return;
        }
        requestInFlight.current = true;
        setBusy(true);
        setError("");
        try {
            const response = await auth.reauthenticate({
                password,
                otpChannel: channel,
                ...(challenge && !resend ? { otp } : {}),
            });
            if (!mounted.current) return;
            if (response?.requiresOtp) {
                setChallenge(response);
                setOtpChannel(response.otpChannel || channel);
                setOtp("");
                setResendSeconds(30);
            } else if (response?.accessToken) {
                setPassword("");
                setOtp("");
                setShowPassword(false);
            } else {
                setError("Your session could not be renewed. Please try again.");
            }
        } catch (err) {
            if (mounted.current) setError(recoveryError(err, !!challenge));
        } finally {
            requestInFlight.current = false;
            if (mounted.current) setBusy(false);
        }
    };

    const signOut = async () => {
        if (requestInFlight.current) return;
        requestInFlight.current = true;
        setBusy(true);
        setPassword("");
        setOtp("");
        setShowPassword(false);
        try {
            await auth.endSession(reason === "account_changed" ? "account_changed" : "logout");
        } finally {
            // endSession() normally navigates away; this only runs if it couldn't
            // (e.g. storage threw, or another tab's fresher session won the race).
            // Without it the modal would stay disabled with no way to dismiss it.
            requestInFlight.current = false;
            if (mounted.current) setBusy(false);
        }
    };

    return (
        <Modal
            isOpen
            centered
            backdrop="static"
            keyboard={false}
            autoFocus={false}
            trapFocus
            returnFocusAfterClose
            fade={false}
            zIndex={11000}
            labelledBy="rdc-session-recovery-title"
            innerRef={describeRecoveryDialog}
            className="rdc-session-recovery"
            onOpened={focusInput}
        >
            <ModalHeader id="rdc-session-recovery-title" tag="h2">
                {confirmSignOut ? "Sign out and leave this page?" : "Continue your session"}
            </ModalHeader>
            <ModalBody>
                {confirmSignOut ? (
                    <>
                        <p id="rdc-session-recovery-description">
                            Signing out will leave this page. Any unsaved changes may be lost.
                            Enter your password instead to continue where you left off.
                        </p>
                        <div className="rdc-session-recovery__actions">
                            <button
                                ref={keepWorkingRef}
                                type="button"
                                className="btn btn-primary"
                                disabled={busy}
                                onClick={() => setConfirmSignOut(false)}
                            >
                                Keep working
                            </button>
                            <button type="button" className="btn btn-outline-danger" disabled={busy} onClick={signOut}>
                                {busy ? "Signing out..." : "Sign out and leave"}
                            </button>
                        </div>
                    </>
                ) : (
                    <form onSubmit={(event) => { event.preventDefault(); renewSession(); }} aria-busy={busy}>
                        <p id="rdc-session-recovery-description">
                            {reason === "session_timeout"
                                ? "Your session paused after a period of inactivity. "
                                : reason === "account_changed"
                                    ? "Another tab changed accounts. Verify the account shown below to resume this page. "
                                    : "Please verify your identity to renew your session. "}
                            Your page is still open. Enter your password to continue where you left off.
                        </p>
                        <p className="rdc-session-recovery__account">
                            <span className="text-muted">Signed in as </span>
                            <strong>{email || "your current account"}</strong>
                        </p>
                        {email && <input type="hidden" name="username" autoComplete="username" value={email} />}
                        <p className="small text-muted">Keep this tab open. Unsaved changes have not been saved automatically.</p>
                        {!challenge ? (
                            <div className="mb-3">
                                <label className="form-label" htmlFor="rdc-session-password">Password</label>
                                <div className="input-group">
                                    <input
                                        ref={passwordRef}
                                        autoFocus
                                        id="rdc-session-password"
                                        name="password"
                                        className="form-control"
                                        type={showPassword ? "text" : "password"}
                                        autoComplete="current-password"
                                        required
                                        value={password}
                                        disabled={busy}
                                        aria-invalid={!!error}
                                        aria-describedby={error ? "rdc-session-recovery-error" : undefined}
                                        onChange={(event) => { setPassword(event.target.value); setError(""); }}
                                    />
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary"
                                        aria-label={showPassword ? "Hide password" : "Show password"}
                                        aria-pressed={showPassword}
                                        disabled={busy}
                                        onClick={() => setShowPassword((shown) => !shown)}
                                    >
                                        {showPassword ? "Hide" : "Show"}
                                    </button>
                                </div>
                            </div>
                        ) : (
                            <div className="mb-3">
                                <p role="status">{challenge.message || `Enter the code sent to your ${otpChannel === "sms" ? "phone" : "email"}.`}</p>
                                <label className="form-label" htmlFor="rdc-session-otp">Verification code</label>
                                <input
                                    ref={otpRef}
                                    autoFocus
                                    id="rdc-session-otp"
                                    name="otp"
                                    className="form-control"
                                    type="text"
                                    inputMode="numeric"
                                    autoComplete="one-time-code"
                                    pattern="[0-9]{6}"
                                    maxLength={6}
                                    required
                                    disabled={busy}
                                    value={otp}
                                    aria-invalid={!!error}
                                    aria-describedby={error ? "rdc-session-recovery-error" : undefined}
                                    onChange={(event) => { setOtp(event.target.value.replace(/\D/g, "").slice(0, 6)); setError(""); }}
                                />
                                <div className="rdc-session-recovery__code-actions">
                                    <button type="button" className="btn btn-link p-0" disabled={busy || resendSeconds > 0} onClick={() => renewSession({ resend: true })}>
                                        {resendSeconds > 0 ? `Resend code in ${resendSeconds}s` : "Resend code"}
                                    </button>
                                    <button type="button" className="btn btn-link p-0" disabled={busy} onClick={() => renewSession({ channel: otpChannel === "sms" ? "email" : "sms", resend: true })}>
                                        Send by {otpChannel === "sms" ? "email" : "SMS"} instead
                                    </button>
                                    <button type="button" className="btn btn-link p-0" disabled={busy} onClick={() => { setChallenge(null); setOtp(""); setError(""); }}>
                                        Re-enter password
                                    </button>
                                </div>
                            </div>
                        )}
                        {error && <p id="rdc-session-recovery-error" className="alert alert-danger" role="alert">{error}</p>}
                        <div className="rdc-session-recovery__actions">
                            <button type="submit" className="btn btn-primary" disabled={busy}>
                                {busy ? "Verifying..." : challenge ? "Verify and continue" : "Continue session"}
                            </button>
                            <button type="button" className="btn btn-link" disabled={busy} onClick={() => setConfirmSignOut(true)}>
                                Sign out
                            </button>
                        </div>
                    </form>
                )}
            </ModalBody>
        </Modal>
    );
};

SessionRecoveryModal.propTypes = { reason: PropTypes.string, email: PropTypes.string };

export default SessionRecoveryModal;
